import React, { useEffect, useMemo, useRef, useState } from "react";
import { Ic } from "./Icon";

export interface Option {
  value: string;
  label: string;
  hint?: string;
}

interface Props {
  value: string;
  onChange: (next: string) => void;
  options: (Option | string)[];
  placeholder?: string;
  style?: React.CSSProperties;
  width?: number | string;
}

export function Select({
  value, onChange, options, placeholder = "Select…", style, width,
}: Props) {
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const popRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLDivElement>(null);

  const opts: Option[] = useMemo(
    () => options.map((o) => typeof o === "string" ? { value: o, label: o } : o),
    [options],
  );
  const current = opts.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape")    { e.preventDefault(); setOpen(false); }
      else if (e.key === "ArrowDown") { e.preventDefault(); setHi(h => Math.min(opts.length - 1, h + 1)); }
      else if (e.key === "ArrowUp")   { e.preventDefault(); setHi(h => Math.max(0, h - 1)); }
      else if (e.key === "Enter")     { e.preventDefault(); onChange(opts[hi].value); setOpen(false); }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, opts, hi, onChange]);

  return (
    <div style={{ position: "relative", display: "inline-block", width: width ?? "auto" }}>
      <div
        ref={btnRef}
        className="input"
        style={{ cursor: "pointer", userSelect: "none", ...style }}
        onClick={() => {
          setOpen(o => !o);
          const i = opts.findIndex(o => o.value === value);
          if (i >= 0) setHi(i);
        }}
      >
        <span style={{
          flex: 1, color: current ? "var(--ink)" : "var(--ink-3)",
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>{current ? current.label : placeholder}</span>
        <Ic name="chevDown" size={12} />
      </div>
      {open && (
        <div ref={popRef} style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0,
          minWidth: "100%", background: "var(--surface)", borderRadius: 8,
          boxShadow: "var(--shadow-pop)", padding: 4, zIndex: 200,
          maxHeight: 280, overflowY: "auto",
        }}>
          {opts.map((o, i) => (
            <div
              key={o.value}
              onClick={() => { onChange(o.value); setOpen(false); }}
              onMouseEnter={() => setHi(i)}
              style={{
                padding: "7px 10px", borderRadius: 5, cursor: "pointer",
                fontSize: 13,
                background: i === hi ? "var(--bg)" : "transparent",
                display: "flex", alignItems: "center", gap: 8,
              }}
            >
              <span style={{
                flex: 1,
                color: o.value === value ? "var(--ink)" : "var(--ink-2)",
                fontWeight: o.value === value ? 500 : 400,
              }}>{o.label}</span>
              {o.hint && (
                <span style={{ color: "var(--ink-3)", fontSize: 11 }}>{o.hint}</span>
              )}
              {o.value === value && <Ic name="check" size={12} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
